/**
 * Loading Store Module
 *
 * Manages loading state centrally via reference counting.
 * Prevents flickering from concurrent requests by tracking
 * individual request lifecycle rather than a boolean toggle.
 *
 * Loading types:
 *  - 'progress'    → Shows top progress bar (default for API calls)
 *  - 'fullscreen'  → Shows full-screen AppLoader overlay
 *  - 'background'  → Silent, no UI change
 *  - 'silent'      → Alias for background
 */

export default {
  namespaced: true,

  state: () => ({
    // Reference counter per loading type
    counts: {
      progress: 0,
      fullscreen: 0,
      background: 0,
      silent: 0,
    },
    // Manual app-loader override (for login → dashboard transition)
    appLoaderVisible: false,
    // Context message shown in AppLoader
    appLoaderMessage: 'Loading workspace...',
  }),

  getters: {
    /**
     * True when any progress-type request is in flight.
     * Drives the AppProgressBar.
     */
    isLoading: (state) =>
      state.counts.progress > 0 || state.appLoaderVisible,

    /**
     * True when a fullscreen loader should appear.
     * Driven by manual overrides or fullscreen-type requests.
     */
    isAppLoading: (state) =>
      state.appLoaderVisible || state.counts.fullscreen > 0,

    /**
     * The contextual message shown inside AppLoader.
     */
    appLoaderMessage: (state) => state.appLoaderMessage,

    /**
     * Raw counts — useful for debugging or advanced UI.
     */
    counts: (state) => state.counts,
  },

  mutations: {
    /**
     * Increment reference counter for a given loading type.
     * @param {string} type - 'progress' | 'fullscreen' | 'background' | 'silent'
     */
    increment(state, type = 'progress') {
      const key = type === 'silent' ? 'background' : type;
      if (Object.prototype.hasOwnProperty.call(state.counts, key)) {
        state.counts[key]++;
      }
    },

    /**
     * Decrement reference counter for a given loading type.
     * Guarded: never goes below zero.
     */
    decrement(state, type = 'progress') {
      const key = type === 'silent' ? 'background' : type;
      if (Object.prototype.hasOwnProperty.call(state.counts, key)) {
        state.counts[key] = Math.max(0, state.counts[key] - 1);
      }
    },

    /**
     * Reset a specific counter (e.g. on navigation or error recovery).
     */
    reset(state, type) {
      if (type) {
        const key = type === 'silent' ? 'background' : type;
        if (Object.prototype.hasOwnProperty.call(state.counts, key)) {
          state.counts[key] = 0;
        }
      } else {
        // Reset all
        Object.keys(state.counts).forEach((k) => {
          state.counts[k] = 0;
        });
      }
    },

    /**
     * Show the full-screen AppLoader with a contextual message.
     */
    showAppLoader(state, message = 'Loading workspace...') {
      state.appLoaderMessage = message;
      state.appLoaderVisible = true;
    },

    /**
     * Hide the full-screen AppLoader.
     */
    hideAppLoader(state) {
      state.appLoaderVisible = false;
    },
  },
};
